import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type SyntheticEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Button type that submits the form; a constant because the i18n lint rule flags string attributes. */
const SUBMIT = 'submit';

/** Props of {@link PhoneStep}. */
export interface PhoneStepProps {
  /** `choose`: share or type; `typed`: the field (S4 R14). */
  readonly mode: 'choose' | 'typed';
  readonly pending: boolean;
  /** The trader tapped "Share my number" (never called automatically). */
  readonly onShare: () => void;
  /** The trader chose to type the number. */
  readonly onType: () => void;
  /** The typed text, as is: the hook normalizes it. */
  readonly onSubmit: (input: string) => void;
}

/**
 * Phone step of E2 (specs/S4-telegram-login/spec.md R14, Proposal P1): Telegram's native contact
 * request first, a typed field as the fallback. The field starts empty (I7).
 */
export function PhoneStep({
  mode,
  pending,
  onShare,
  onType,
  onSubmit,
}: PhoneStepProps): ReactElement {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (mode === 'typed') field.current?.focus();
  }, [mode]);

  function submit(event: SyntheticEvent): void {
    event.preventDefault();
    if (!pending) onSubmit(value);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography
        component="h1"
        sx={{ fontSize: 26, lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700 }}
      >
        {t('onboarding.telegram.phone.title')}
      </Typography>
      <Typography sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
        {t('onboarding.telegram.phone.help')}
      </Typography>
      {mode === 'choose' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={pending}
            onClick={onShare}
            sx={{ minHeight: 52 }}
          >
            {t('onboarding.telegram.phone.share')}
          </Button>
          <Button variant="text" onClick={onType} sx={{ minHeight: 44 }}>
            {t('onboarding.telegram.phone.typeInstead')}
          </Button>
        </Box>
      ) : (
        <Box
          component="form"
          noValidate
          onSubmit={submit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            label={t('onboarding.telegram.phone.label')}
            helperText={t('onboarding.telegram.phone.hint')}
            type="tel"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
            inputRef={field}
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'tel', autoComplete: 'tel' } }}
          />
          <Button
            type={SUBMIT}
            variant="contained"
            size="large"
            fullWidth
            disabled={pending || value.trim() === ''}
            sx={{ minHeight: 52 }}
          >
            {t('onboarding.telegram.phone.submit')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
